import { apiClient, authFetch, API_BASE_URL } from './apiClient';

const BASE = `${API_BASE_URL}/api/finance`;

export const getTreasuryAccounts = async () => {
  return apiClient(`${BASE}/accounts`);
};

export const getTreasuryAccount = async (accountId) => {
  const accounts = await getTreasuryAccounts();
  return accounts.find(a => a.id === accountId);
};

export const getTreasuryEntries = async (params = {}) => {
  return apiClient(`${BASE}/entries${toQuery(params)}`);
};

export const getEntriesByAccount = async (accountId) => {
  return apiClient(`${BASE}/entries?account_id=${accountId}`);
};

export const getCashFlowAccounts = async () => {
  return apiClient(`${BASE}/chart-accounts`);
};

export const getCashFlowAccountsByCategory = async () => {
  const accounts = await getCashFlowAccounts();
  const grouped = {};
  accounts.forEach(a => {
    const cat = a.category || 'Sin categoría';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(a);
  });
  return grouped;
};

export const getTreasurySummary = async () => {
  return apiClient(`${BASE}/summary`);
};

export const getCashFlowSummary = async () => {
  return apiClient(`${BASE}/summary`);
};

export const createTreasuryEntry = async (entryData) => {
  return apiClient(`${BASE}/entries`, {
    method: 'POST',
    body: JSON.stringify(entryData),
  });
};

export const setTreasuryEntriesStatus = async (entryIds, reconciliationStatus) => {
  return apiClient(`${BASE}/entries/bulk-status`, {
    method: 'POST',
    body: JSON.stringify({ ids: entryIds, reconciliation_status: reconciliationStatus }),
  });
};

export const deleteTreasuryEntries = async (entryIds) => {
  return apiClient(`${BASE}/entries/bulk-delete`, {
    method: 'POST',
    body: JSON.stringify({ ids: entryIds }),
  });
};

export const importBankStatements = async (accountId, files) => {
  const formData = new FormData();
  formData.append('account_id', String(accountId));
  (Array.isArray(files) ? files : [files]).forEach((file) => {
    if (file) formData.append('files', file);
  });
  return apiClient(`${BASE}/entries/import`, {
    method: 'POST',
    body: formData,
    timeoutMs: 120000,
  });
};

export const conciliateEntry = async (entryId) => setTreasuryEntriesStatus([entryId], 'Conciliado');

export const getTreasuryDocumentSuggestions = async (entryIds = []) => {
  return apiClient(`${BASE}/entries/document-suggestions`, {
    method: 'POST',
    body: JSON.stringify({ ids: entryIds }),
    timeoutMs: 60000,
  });
};

export const getTreasuryDocumentCandidates = async (entryId, search = '') => {
  return apiClient(`${BASE}/entries/${entryId}/document-candidates${toQuery({
    search: search || undefined,
  })}`);
};

export const setTreasuryDocumentLink = async (entryId, payload) => {
  return apiClient(`${BASE}/entries/${entryId}/document-link`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const createTreasuryAccount = async (accountData) => {
  return apiClient(`${BASE}/accounts`, {
    method: 'POST',
    body: JSON.stringify(accountData),
  });
};

export const updateTreasuryAccount = async (accountId, accountData) => {
  return apiClient(`${BASE}/accounts/${accountId}`, {
    method: 'PUT',
    body: JSON.stringify(accountData),
  });
};

export const deleteTreasuryAccount = async (accountId) => {
  return apiClient(`${BASE}/accounts/${accountId}`, {
    method: 'DELETE',
  });
};

export const createCashFlowAccount = async (accountData) => {
  return apiClient(`${BASE}/chart-accounts`, {
    method: 'POST',
    body: JSON.stringify(accountData),
  });
};

export const updateCashFlowAccount = async (accountId, accountData) => {
  return apiClient(`${BASE}/chart-accounts/${accountId}`, {
    method: 'PUT',
    body: JSON.stringify(accountData),
  });
};

export const deleteCashFlowAccount = async (accountId) => {
  return apiClient(`${BASE}/chart-accounts/${accountId}`, {
    method: 'DELETE',
  });
};

export const updateTreasuryEntry = async (entryId, entryData) => {
  return apiClient(`${BASE}/entries/${entryId}`, {
    method: 'PUT',
    body: JSON.stringify(entryData),
  });
};

export const deleteTreasuryEntry = async (entryId) => {
  return apiClient(`${BASE}/entries/${entryId}`, {
    method: 'DELETE',
  });
};

export const assignCashFlowToEntries = async (entryIds, cashFlowAccountId) => {
  console.warn('Cash flow assignment not implemented yet');
  return { success: false, message: 'Not implemented' };
};

export const getChartAccountLevels = async () => {
  return apiClient(`${BASE}/chart-account-levels`);
};

export const updateChartAccountLevels = async (levels) => {
  return apiClient(`${BASE}/chart-account-levels`, {
    method: 'PUT',
    body: JSON.stringify({ levels }),
  });
};

export const getDetailNodes = async () => {
  return apiClient(`${BASE}/detail-nodes`);
};

export const createDetailNode = async (payload) => {
  return apiClient(`${BASE}/detail-nodes`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const getChartAccounts = async () => {
  return apiClient(`${BASE}/chart-accounts`);
};

export const createChartAccount = async (payload) => {
  return apiClient(`${BASE}/chart-accounts`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateChartAccount = async (chartAccountId, payload) => {
  return apiClient(`${BASE}/chart-accounts/${chartAccountId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteChartAccount = async (chartAccountId) => {
  return apiClient(`${BASE}/chart-accounts/${chartAccountId}`, {
    method: 'DELETE',
  });
};

export const getAccountingRules = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.category) query.set('category', params.category);
  if (params.trigger_event) query.set('trigger_event', params.trigger_event);
  if (params.status) query.set('status', params.status);
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return apiClient(`${BASE}/accounting-rules${suffix}`);
};

export const getAccountingRule = async (ruleId) => {
  return apiClient(`${BASE}/accounting-rules/${ruleId}`);
};

export const createAccountingRule = async (payload) => {
  return apiClient(`${BASE}/accounting-rules`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateAccountingRule = async (ruleId, payload) => {
  return apiClient(`${BASE}/accounting-rules/${ruleId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteAccountingRule = async (ruleId) => {
  return apiClient(`${BASE}/accounting-rules/${ruleId}`, {
    method: 'DELETE',
  });
};

const toQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.set(key, value);
  });
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return suffix;
};

export const getJournalEntries = async (params = {}) => {
  return apiClient(`${BASE}/journal-entries${toQuery(params)}`);
};

export const getJournalEntry = async (entryId) => {
  return apiClient(`${BASE}/journal-entries/${entryId}`);
};

export const createJournalEntry = async (payload) => {
  return apiClient(`${BASE}/journal-entries`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateJournalEntry = async (entryId, payload) => {
  return apiClient(`${BASE}/journal-entries/${entryId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteJournalEntry = async (entryId) => {
  return apiClient(`${BASE}/journal-entries/${entryId}`, {
    method: 'DELETE',
  });
};

export const getLedger = async (params = {}) => {
  return apiClient(`${BASE}/ledger${toQuery(params)}`);
};

export const getFinancialStatements = async (params = {}) => {
  return apiClient(`${BASE}/statements${toQuery(params)}`);
};

export const getFinancialStatementsComparative = async (params = {}) => {
  return apiClient(`${BASE}/statements/comparative${toQuery(params)}`);
};

export const getRegisterDocuments = async (params = {}) => {
  return apiClient(`${BASE}/registers${toQuery(params)}`);
};

export const getSaleDocumentCandidates = async (saleId, search = '') => {
  return apiClient(`${BASE}/registers/sale-link-candidates${toQuery({
    sale_id: saleId,
    search: search || undefined,
  })}`);
};

export const getDocumentSaleCandidates = async (docId, search = '') => {
  return apiClient(`${BASE}/registers/${docId}/sale-candidates${toQuery({
    search: search || undefined,
  })}`);
};

export const getSaleLinkSuggestions = async (params = {}) => {
  return apiClient(`${BASE}/registers/sale-link-suggestions${toQuery(params)}`);
};

export const setRegisterSaleLink = async (docId, payload) => {
  return apiClient(`${BASE}/registers/${docId}/sale-link`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const importVentasFromConsolidado = async (files, { skip_duplicates = true } = {}) => {
  const formData = new FormData();
  const list = Array.isArray(files) ? files : [files];
  list.forEach((file) => {
    if (file) formData.append('files', file);
  });
  formData.append('skip_duplicates', skip_duplicates ? 'true' : 'false');
  return apiClient(`${BASE}/registers/import-ventas`, {
    method: 'POST',
    body: formData,
    timeoutMs: 120000,
  });
};

export const importComprasFromConsolidado = async (files, { skip_duplicates = true } = {}) => {
  const formData = new FormData();
  const list = Array.isArray(files) ? files : [files];
  list.forEach((file) => {
    if (file) formData.append('files', file);
  });
  formData.append('skip_duplicates', skip_duplicates ? 'true' : 'false');
  return apiClient(`${BASE}/registers/import-compras`, {
    method: 'POST',
    body: formData,
    timeoutMs: 120000,
  });
};

export const exportVentasRegisterXlsx = async (payload = {}) => {
  const response = await authFetch(`${BASE}/registers/export-ventas`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    timeoutMs: 120000,
  });
  if (!response.ok) {
    let detail = `Error ${response.status}: ${response.statusText}`;
    try {
      const data = await response.json();
      if (data?.detail) {
        detail = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
      }
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const blob = await response.blob();
  const disposition = String(response.headers.get('content-disposition') || '');
  const match = disposition.match(/filename="?([^"]+)"?/i);
  const filename = match?.[1] || 'libro_ventas.xlsx';
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return { filename, size: blob.size };
};

export const createRegisterDocument = async (payload) => {
  return apiClient(`${BASE}/registers`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateRegisterDocument = async (docId, payload) => {
  return apiClient(`${BASE}/registers/${docId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const deleteRegisterDocument = async (docId) => {
  return apiClient(`${BASE}/registers/${docId}`, {
    method: 'DELETE',
  });
};

export const getExpenseReports = async (params = {}) => {
  return apiClient(`${BASE}/expense-reports${toQuery(params)}`);
};

export const getExpenseReport = async (reportId) => {
  return apiClient(`${BASE}/expense-reports/${reportId}`);
};

export const createExpenseReport = async (payload) => {
  return apiClient(`${BASE}/expense-reports`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
};

export const updateExpenseReport = async (reportId, payload) => {
  return apiClient(`${BASE}/expense-reports/${reportId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
};

export const approveExpenseReport = async (reportId) => {
  return apiClient(`${BASE}/expense-reports/${reportId}/approve`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
};

export const rejectExpenseReport = async (reportId, reason = '') => {
  return apiClient(`${BASE}/expense-reports/${reportId}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
};

export const deleteExpenseReport = async (reportId) => {
  return apiClient(`${BASE}/expense-reports/${reportId}`, {
    method: 'DELETE',
  });
};

export const getExpenseReportItems = async () => {
  return apiClient(`${BASE}/expense-report-items`);
};
